import React, { useEffect, useState } from "react";
import { X, Copy, Check, Sparkles, Megaphone, Gift, AlertTriangle, Wrench, Rocket, Tag, Info, Heart, Calendar } from "lucide-react";
import api from "../lib/api";

const ICONS = { sparkles: Sparkles, megaphone: Megaphone, gift: Gift, "alert-triangle": AlertTriangle, wrench: Wrench, rocket: Rocket, tag: Tag, info: Info, heart: Heart, calendar: Calendar };
export const THEME_STYLE = {
  teal: "bg-[#E6F4F1] text-[#0F5132] border-[#CBE8E2]",
  info: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]",
  amber: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]",
  red: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]",
  violet: "bg-[#F5F3FF] text-[#5B21B6] border-[#DDD6FE]",
  slate: "bg-[#F8FAFC] text-[#334155] border-[#E2E8F0]",
};

export function pageGroup(pathname) {
  if (pathname === "/" || pathname === "") return "landing";
  if (pathname.startsWith("/pricing")) return "pricing";
  if (pathname.startsWith("/names")) return "names";
  if (pathname.startsWith("/community")) return "community";
  if (pathname.startsWith("/register") || pathname.startsWith("/login")) return "register";
  if (pathname.startsWith("/app/billing")) return "app_billing";
  if (pathname === "/app" || pathname.startsWith("/app/dashboard")) return "app_dashboard";
  if (pathname.startsWith("/app")) return "app_other";
  return "all";
}

// ---- Analytics: one anonymous viewer id, batched events, reliable send on navigation ----
function viewerId() {
  try {
    let v = localStorage.getItem("pl_viewer_id");
    if (!v) { v = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`); localStorage.setItem("pl_viewer_id", v); }
    return v;
  } catch { return ""; }
}

let eventQueue = [];
let flushTimer = null;
function flushEvents() {
  const events = eventQueue.splice(0);
  if (!events.length) return;
  const body = { viewer: viewerId(), events };
  try {
    const url = `${api.defaults.baseURL}/banners/events`;
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([JSON.stringify(body)], { type: "application/json" }))) return;
  } catch { /* fall through to axios */ }
  api.post("/banners/events", body).catch(() => {});
}
export function trackBanner(bannerId, type, immediate = false) {
  eventQueue.push({ banner_id: bannerId, type });
  clearTimeout(flushTimer);
  if (immediate) flushEvents();
  else flushTimer = setTimeout(flushEvents, 1500);
}

// Attribution: the most recent banner interaction (CTA click or code copy) within 7 days
// is credited if the user then starts a checkout.
export function bannerAttribution() {
  try {
    const a = JSON.parse(localStorage.getItem("pl_banner_attr") || "null");
    if (a && Date.now() - a.at < 7 * 864e5) return a.banner_id;
  } catch { /* noop */ }
  return null;
}
function setAttribution(bannerId) {
  try { localStorage.setItem("pl_banner_attr", JSON.stringify({ banner_id: bannerId, at: Date.now() })); } catch { /* noop */ }
}

function PromoChip({ banner }) {
  const [copied, setCopied] = useState(false);
  if (!banner.promotion_code) return null;
  const copy = async () => {
    try { await navigator.clipboard.writeText(banner.promotion_code); } catch { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    trackBanner(banner.id, "promo_copy", true);
    setAttribution(banner.id);
  };
  return (
    <button onClick={copy} data-testid={`site-banner-copy-${banner.id}`} aria-label={`Copy code ${banner.promotion_code}`}
      className="ml-1 flex shrink-0 items-center gap-1 rounded-full border border-current/20 bg-white/60 px-2 py-0.5 font-mono text-xs font-semibold hover:bg-white/90">
      {banner.promotion_code} {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3 opacity-60" />}
    </button>
  );
}

export function BannerBar({ banner, onDismiss }) {
  const Icon = ICONS[banner.icon] || Sparkles;
  return (
    <div data-testid={`site-banner-${banner.id}`} role="status" className={`flex items-center justify-center gap-2 border-b px-4 py-2 text-center text-sm ${THEME_STYLE[banner.theme] || THEME_STYLE.teal}`}>
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="min-w-0 max-sm:line-clamp-2 max-sm:text-xs">
        {banner.title && <strong className="mr-1">{banner.title}</strong>}
        {banner.message}
        {banner.subtitle && <span className="ml-1 opacity-80">{banner.subtitle}</span>}
      </span>
      <PromoChip banner={banner} />
      {banner.cta_label && banner.cta_url && (
        <a href={banner.cta_url} data-testid={`site-banner-cta-${banner.id}`}
          onClick={() => { trackBanner(banner.id, "cta_click", true); setAttribution(banner.id); }}
          className="ml-1 shrink-0 rounded-full bg-white/70 px-2.5 py-0.5 text-xs font-semibold underline-offset-2 hover:underline">{banner.cta_label}</a>
      )}
      {banner.dismissible && (
        <button data-testid={`site-banner-dismiss-${banner.id}`} onClick={onDismiss} aria-label="Dismiss announcement" className="ml-1 shrink-0 rounded-full p-0.5 opacity-70 hover:opacity-100"><X className="h-4 w-4" /></button>
      )}
    </div>
  );
}

function markImpressions(banners) {
  let seen;
  try { seen = JSON.parse(sessionStorage.getItem("pl_banners_seen") || "[]"); } catch { seen = []; }
  const fresh = banners.filter((b) => !seen.includes(b.id));
  if (!fresh.length) return;
  fresh.forEach((b) => trackBanner(b.id, "impression"));
  try { sessionStorage.setItem("pl_banners_seen", JSON.stringify([...seen, ...fresh.map((b) => b.id)])); } catch { /* noop */ }
}

function useBanners(endpoint) {
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("pl_banners_dismissed") || "[]"); } catch { return []; }
  });
  useEffect(() => {
    let live = true;
    const page = pageGroup(window.location.pathname);
    api.get(endpoint, { params: { page } }).then((r) => {
      if (!live) return;
      const list = r.data.banners || [];
      setBanners(list);
      markImpressions(list);
    }).catch(() => {});
    return () => { live = false; };
  }, [endpoint]);
  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    trackBanner(id, "dismiss", true);
    try { sessionStorage.setItem("pl_banners_dismissed", JSON.stringify(next)); } catch { /* noop */ }
  };
  return { banners: banners.filter((b) => !dismissed.includes(b.id)), dismiss };
}

export function PublicBanners() {
  const { banners, dismiss } = useBanners("/banners/active");
  if (!banners.length) return null;
  return <div data-testid="public-banners" aria-label="Site announcements">{banners.map((b) => <BannerBar key={b.id} banner={b} onDismiss={() => dismiss(b.id)} />)}</div>;
}

export function InternalBanners() {
  const { banners, dismiss } = useBanners("/banners/for-me");
  if (!banners.length) return null;
  return <div data-testid="internal-banners" aria-label="Site announcements">{banners.map((b) => <BannerBar key={b.id} banner={b} onDismiss={() => dismiss(b.id)} />)}</div>;
}
